import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useLocation} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {Button, Container, Header, List, Loader, Message, Segment} from 'semantic-ui-react';

import {
  McpAuthorizationRequest,
  approveMcpAuthorization,
  denyMcpAuthorization,
  getMcpAuthorizationRequest
} from '../../services/mcpApi';

type ConsentViewProps = {
  request?: McpAuthorizationRequest;
  loading: boolean;
  busy: boolean;
  error?: string;
  onAllow: () => void;
  onDeny: () => void;
};

const scopeCopy: Record<string, string> = {
  'messages:read': 'Read your message conversations',
  'messages:write': 'Prepare reply drafts',
  'admin:usage:read': 'Read aggregate product usage and admin email delivery metrics across BetaReader',
  'admin:moderation:read': 'Read moderation reports, account context, conversations and blocked message evidence',
  'admin:moderation:write': 'Change moderation case status and restrict, suspend or restore reported accounts'
};

export const AuthorizationConsentView: React.FC<ConsentViewProps> = ({
  request,
  loading,
  busy,
  error,
  onAllow,
  onDeny
}) => {
  const {t} = useTranslation();
  if (loading) return <Loader active inline="centered" />;
  return (
    <Container text>
      <Segment padded>
        <Header as="h1">
          {t('McpAuthorizationTitle', '{{client}} wants access to BetaReader', {
            client: request?.clientName ?? t('ConnectedApplication', 'Connected application')
          })}
        </Header>
        {error && <Message negative content={error} />}
        {request && (
          <>
            <Header as="h2" size="small">{request.clientName}</Header>
            <List bulleted relaxed>
              {request.scopes.map(scope => (
                <List.Item key={scope}>
                  {t(`McpScope_${scope}`, scopeCopy[scope] ?? scope)}
                </List.Item>
              ))}
            </List>
            <Message info>
              {request.scopes.some(scope => scope.startsWith('admin:moderation:'))
                ? 'This connection can access sensitive report and message content. If you grant moderation write access, it can restrict, suspend or restore the reported account and change case status. These actions are logged.'
                : request.scopes.includes('admin:usage:read')
                ? 'This connection can read product-wide aggregate counts and admin campaign delivery summaries. It cannot access user identities, recipient addresses, message content, or manuscripts.'
                : t(
                    'McpAuthorizationBoundary',
                    'This app acts as your BetaReader user. It cannot access other members’ messages or download manuscripts.'
                  )}
            </Message>
            <Message warning>
              {t(
                'McpAuthorizationReturnTo',
                'After approval, BetaReader will return you to:'
              )}{' '}
              <strong>{request.redirectOrigin}</strong>
            </Message>
            <Button
              type="button"
              disabled={busy}
              content={t('Cancel')}
              onClick={onDeny}
            />
            <Button
              type="button"
              primary
              disabled={busy}
              loading={busy}
              content={t('McpAllowAccess', 'Allow access')}
              onClick={onAllow}
            />
          </>
        )}
      </Segment>
    </Container>
  );
};

const AuthorizationConsent: React.FC = () => {
  const {t} = useTranslation();
  const location = useLocation();
  const activeAccount = useSelector((state: any) => state.account.activeAccount);
  const userProfile = useSelector((state: any) => state.user.userProfile);
  const userId = userProfile?._id;
  const requestId = new URLSearchParams(location.search).get('request_id');
  const accountId = activeAccount?._id ?? userProfile?.account?._id;
  const invalidRequestMessage = t('McpAuthorizationInvalid', 'This authorization request is invalid or expired.');
  const [request, setRequest] = useState<McpAuthorizationRequest>();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let current = true;
    if (!userId) {
      return () => {
        current = false;
      };
    }
    if (!requestId || !accountId) {
      setError(invalidRequestMessage);
      setLoading(false);
      return () => {
        current = false;
      };
    }
    setLoading(true);
    setError(undefined);
    setRequest(undefined);
    getMcpAuthorizationRequest(requestId, accountId)
      .then(value => {
        if (current) setRequest(value);
      })
      .catch(reason => {
        if (current) setError(reason.message);
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [accountId, invalidRequestMessage, requestId, userId]);

  const decide = async (approved: boolean) => {
    if (!requestId || !accountId || busy) return;
    setBusy(true);
    setError(undefined);
    try {
      const result = approved
        ? await approveMcpAuthorization(requestId, accountId)
        : await denyMcpAuthorization(requestId, accountId);
      window.location.assign(result.redirect_uri);
    } catch (reason: any) {
      setError(reason.message);
      setBusy(false);
    }
  };

  if (!userProfile) return <Loader active inline="centered" />;

  return (
    <AuthorizationConsentView
      request={request}
      loading={loading}
      busy={busy}
      error={error}
      onAllow={() => decide(true)}
      onDeny={() => decide(false)}
    />
  );
};

export default AuthorizationConsent;
